import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Users } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import type { PatientSummaryItem } from '@/hooks/useCompanyAnalytics';

interface Props {
  patients: PatientSummaryItem[];
}

export function PatientSummaryTable({ patients }: Props) {
  const { t } = useTranslation();

  if (patients.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          {t('analytics.patientSummary')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('analytics.patient')}</TableHead>
              <TableHead className="text-center">{t('analytics.completion')}</TableHead>
              <TableHead className="text-center">{t('analytics.executed')}</TableHead>
              <TableHead className="text-center">{t('analytics.alertsCol')}</TableHead>
              <TableHead className="text-center">{t('analytics.incidentsCol')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {patients.map((p) => (
              <TableRow key={p.patient_id}>
                <TableCell>
                  <Link to={`/app/patients/${p.patient_id}`} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                    <SignedAvatar
                      src={p.photo_url}
                      bucket="patient-photos"
                      className="h-7 w-7"
                      fallback={
                        <span className="bg-primary/10 text-primary text-xs flex items-center justify-center w-full h-full">
                          {p.nickname.charAt(0).toUpperCase()}
                        </span>
                      }
                    />
                    <span className="text-sm truncate max-w-[200px] text-primary underline-offset-2 hover:underline">{p.nickname}</span>
                  </Link>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Progress value={p.completion_pct} className="h-2 w-16" />
                    <span className="text-xs text-muted-foreground">{p.completion_pct}%</span>
                  </div>
                </TableCell>
                <TableCell className="text-center text-sm">{p.executions}</TableCell>
                <TableCell className="text-center text-sm">
                  {p.alerts > 0 ? (
                    <span className="text-yellow-600 font-medium">{p.alerts}</span>
                  ) : '0'}
                </TableCell>
                <TableCell className="text-center text-sm">
                  {p.incidents > 0 ? (
                    <span className="text-red-600 font-medium">{p.incidents}</span>
                  ) : '0'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
